import { useState } from "react";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import DocumentList from "@/components/documents/DocumentList";
import DocumentUploadModal from "@/components/documents/DocumentUploadModal";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Upload } from "lucide-react";

const DocumentManagement = () => {
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Calculate progress based on required documents
  const totalRequired = 8; // 3 recommendations + 1 statement + 1 CV + 3 criteria docs
  const uploadedRequired = 3; // Example value
  const progress = (uploadedRequired / totalRequired) * 100;

  const mandatoryDocuments = [
    {
      type: "recommendation",
      label: "Letters of Recommendation",
      required: true,
      count: 3,
      description: "Three letters from academic or professional references on official letterhead",
      formats: ["pdf"],
      maxSize: 5,
      tooltip: "Each letter must be signed and on official letterhead"
    },
    {
      type: "statement",
      label: "Personal Statement",
      required: true,
      description: "A detailed statement outlining your achievements and aspirations",
      formats: ["pdf"],
      maxSize: 5,
      tooltip: "Should be 2-3 pages long and follow our guidelines"
    },
    {
      type: "cv",
      label: "Curriculum Vitae",
      required: true,
      description: "Your detailed CV including all relevant experience",
      formats: ["pdf"],
      maxSize: 5,
      tooltip: "Must include detailed work history and achievements"
    },
    {
      type: "criteria1",
      label: "Evidence of Innovation",
      required: true,
      description: "Documentation of technical innovation or research",
      formats: ["pdf", "jpg", "png"],
      maxSize: 5,
      tooltip: "Patents, research papers, or project documentation"
    },
    {
      type: "criteria2",
      label: "Professional Recognition",
      required: true,
      description: "Awards, certifications, or industry recognition",
      formats: ["pdf", "jpg", "png"],
      maxSize: 5,
      tooltip: "Include certificates and award documentation"
    },
    {
      type: "criteria3",
      label: "Leadership Evidence",
      required: true,
      description: "Documentation of leadership roles and impact",
      formats: ["pdf"],
      maxSize: 5,
      tooltip: "Project leadership, team management evidence"
    },
    {
      type: "criteria4",
      label: "Salary Evidence",
      required: true,
      description: "Proof of meeting salary requirements",
      formats: ["pdf"],
      maxSize: 5,
      tooltip: "Payslips, offer letters, or contracts"
    }
  ];

  const optionalDocuments = [
    {
      type: "publication",
      label: "Publications",
      required: false,
      count: 3,
      description: "Academic or professional publications",
      formats: ["pdf"],
      maxSize: 10,
      tooltip: "Research papers, articles, or publications"
    },
    {
      type: "presentation",
      label: "Presentations",
      required: false,
      count: 3,
      description: "Conference or professional presentations",
      formats: ["pdf", "ppt", "pptx"],
      maxSize: 10,
      tooltip: "Slides or recordings of presentations"
    },
    {
      type: "media",
      label: "Media Coverage",
      required: false,
      count: 3,
      description: "Press coverage or media mentions",
      formats: ["pdf", "jpg", "png"],
      maxSize: 5,
      tooltip: "News articles, interviews, or media features"
    },
    {
      type: "portfolio",
      label: "Project Portfolio",
      required: false,
      description: "Portfolio of significant projects",
      formats: ["pdf"],
      maxSize: 15,
      tooltip: "Detailed documentation of key projects"
    },
    {
      type: "reference",
      label: "Additional References",
      required: false,
      count: 3,
      description: "Additional professional references",
      formats: ["pdf"],
      maxSize: 5,
      tooltip: "Letters from additional referees"
    }
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header Section */}
        <Card>
          <CardHeader>
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div>
                <CardTitle>Document Management</CardTitle>
                <p className="text-muted-foreground mt-1">
                  Upload and manage your application documents
                </p>
              </div>
              <Button onClick={() => setIsUploadModalOpen(true)}>
                <Upload className="mr-2 h-4 w-4" />
                Upload Documents
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Required Documents Progress</span>
                <span>{uploadedRequired} of {totalRequired} uploaded</span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>
          </CardContent>
        </Card>

        {/* Mandatory Documents Section */}
        <DocumentList
          title="Mandatory Documents"
          description="The following documents are required for your application. All documents must be submitted to proceed."
          documents={mandatoryDocuments}
        />

        {/* Optional Documents Section */}
        <DocumentList
          title="Optional Supporting Documents"
          description="Additional documents to strengthen your application (maximum 6 documents)"
          documents={optionalDocuments}
        />

        <DocumentUploadModal
          open={isUploadModalOpen}
          onOpenChange={setIsUploadModalOpen}
          onUploadProgress={setUploadProgress}
        />
      </div>
    </DashboardLayout>
  );
};

export default DocumentManagement;